import { ApiError } from "../../api";
import { formatEpoch, ownValue, safeMessage, tidy } from "../shared/safe";
import { readPageInfo, type PageInfo } from "../facts/factsModel";

// Pure parsing and wording for the "Supply content" screen (POST/GET/PATCH /v1/clients/:id/content, A-routes Phase 3; real examples in
// tests/contract/fixtures/content-dto.json). Everything from the server is untrusted text. Creating content is never approving it:
// a supplied master is a DRAFT until the existing approve route (which re-runs the constraint engine and has no override) accepts it.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const nonEmpty = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);

export const PROVENANCE_CHOICES = ["owner", "ai_assisted", "imported"] as const;
export type ProvenanceChoice = (typeof PROVENANCE_CHOICES)[number];
export const PROVENANCE_CHOICE_LABELS: Record<ProvenanceChoice, string> = {
  owner: "Written by the owner",
  ai_assisted: "Drafted with an AI assistant",
  imported: "Imported from elsewhere",
};
const PROVENANCE_LABELS: Record<string, string> = { ...PROVENANCE_CHOICE_LABELS, generated: "Generated by CloudPush (paid model)", unknown: "Origin not recorded" };
export const provenanceLabel = (kind: string): string => ownValue(PROVENANCE_LABELS, kind) ?? tidy(kind, 40);

export const CONTENT_STATUSES = ["all", "draft", "waiting_review", "approved", "published", "failed"] as const;
export const CONTENT_STATUS_LABELS: Record<string, string> = { all: "All statuses", draft: "Draft", waiting_review: "Waiting for review", approved: "Approved", published: "Published", failed: "Failed" };

export interface ContentItem {
  id: string;
  title: string;
  body: string;
  status: string;
  topic: { title: string; keywords: string[] } | null;
  createdAt: { text: string; iso: string } | null;
  provenance: { kind: string; model: string | null; note: string | null; sourceUrl: string | null; suppliedBy: string | null; identityVerified: boolean | null };
  constraints: { passed: boolean; errors: string[]; warnings: string[] } | null;
  approval: { approvedBy: string | null; approvedAt: { text: string; iso: string } | null } | null;
  approvalRequired: boolean;
}

const strings = (v: unknown, max = 400): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").map((x) => safeMessage(x, max)) : []);

export function readContent(x: unknown): ContentItem | null {
  if (!isObject(x) || !nonEmpty(x.id)) return null;
  const p = isObject(x.provenance) ? x.provenance : {};
  const c = isObject(x.constraints) ? x.constraints : null;
  const t = isObject(x.topic) ? x.topic : null;
  const a = isObject(x.approval) ? x.approval : null;
  return {
    id: x.id as string,
    title: nonEmpty(x.title) ? tidy(x.title, 300) : "(untitled)",
    body: typeof x.body === "string" ? x.body : "",
    status: nonEmpty(x.status) ? tidy(x.status, 30) : "unknown",
    topic: t ? { title: tidy(t.title, 300), keywords: (Array.isArray(t.keywords) ? t.keywords : []).filter((k): k is string => typeof k === "string").map((k) => tidy(k, 80)) } : null,
    createdAt: formatEpoch(x.createdAt),
    provenance: {
      kind: nonEmpty(p.kind) ? tidy(p.kind, 30) : "unknown",
      model: nonEmpty(p.model) ? tidy(p.model, 80) : null,
      note: nonEmpty(p.note) ? tidy(p.note, 600) : null,
      sourceUrl: nonEmpty(p.sourceUrl),
      suppliedBy: nonEmpty(p.suppliedBy) ? tidy(p.suppliedBy, 120) : null,
      identityVerified: typeof p.identityVerified === "boolean" ? p.identityVerified : null,
    },
    // Only an explicit `passed: true` counts as a pass: a missing or malformed check is not one.
    constraints: c ? { passed: c.passed === true, errors: strings(c.errors), warnings: strings(c.warnings) } : null,
    approval: a ? { approvedBy: nonEmpty(a.approvedBy) ? tidy(a.approvedBy, 120) : null, approvedAt: formatEpoch(a.approvedAt) } : null,
    approvalRequired: x.approvalRequired !== false,
  };
}

export function parseContentList(body: unknown): { items: ContentItem[]; page: PageInfo | null } | null {
  if (!isObject(body) || !Array.isArray(body.content)) return null;
  return { items: body.content.map(readContent).filter((c): c is ContentItem => c !== null), page: readPageInfo(body.page) };
}

/** A create/patch reply: the master, and (create only) whether it replayed an earlier request. */
export function parseContentReply(body: unknown): { item: ContentItem; replayed: boolean; approvedFlag: boolean | null } | null {
  if (!isObject(body)) return null;
  const item = readContent(body.content);
  return item ? { item, replayed: body.replayed === true, approvedFlag: typeof body.approved === "boolean" ? body.approved : null } : null;
}

export const isEditable = (status: string): boolean => status === "draft" || status === "waiting_review";

// ---- the create form ---------------------------------------------------------------------------------------------------------------

export interface CreateDraft {
  title: string;
  body: string;
  provenance: ProvenanceChoice;
  provenanceNote: string;
  sourceUrl: string;
  topic: string;
  keywords: string;
}
export const emptyDraft = (): CreateDraft => ({ title: "", body: "", provenance: "owner", provenanceNote: "", sourceUrl: "", topic: "", keywords: "" });

export const splitKeywords = (raw: string): string[] => [...new Set(raw.split(/[,\n]/).map((k) => k.trim()).filter(Boolean))];

/** Problems found before anything is sent (the server validates again and has the last word). */
export function draftProblems(d: CreateDraft): { field: string; message: string }[] {
  const out: { field: string; message: string }[] = [];
  if (d.title.trim() === "") out.push({ field: "title", message: "Enter a title." });
  else if (d.title.trim().length > 200) out.push({ field: "title", message: "At most 200 characters." });
  if (d.body.trim() === "") out.push({ field: "body", message: "Enter the content." });
  if (d.provenance === "imported" && d.sourceUrl.trim() === "" && d.provenanceNote.trim() === "") out.push({ field: "sourceUrl", message: "Imported content needs a source address or a note saying where it came from." });
  if (d.sourceUrl.trim() !== "" && !/^https?:\/\/\S+$/i.test(d.sourceUrl.trim())) out.push({ field: "sourceUrl", message: "The source must be a web address starting with http:// or https://." });
  return out;
}

export function createBody(d: CreateDraft): Record<string, unknown> {
  const kw = splitKeywords(d.keywords);
  return {
    title: d.title.trim(),
    body: d.body,
    provenance: d.provenance,
    ...(d.provenanceNote.trim() ? { provenanceNote: d.provenanceNote.trim() } : {}),
    ...(d.sourceUrl.trim() ? { sourceUrl: d.sourceUrl.trim() } : {}),
    ...(d.topic.trim() ? { topic: d.topic.trim() } : {}),
    ...(kw.length ? { keywords: kw } : {}),
  };
}

/** A short stable fingerprint of a request body: the same content gets the same Idempotency-Key scope, edited content a new one. */
export function fingerprint(body: unknown): string {
  const text = JSON.stringify(body);
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// ---- failures ----------------------------------------------------------------------------------------------------------------------

export interface ContentProblem {
  kind: "constraints" | "validation" | "idempotency_conflict" | "not_editable" | "inactive" | "notfound" | "unauthorized" | "network" | "server" | "other";
  message: string;
  issues: { field: string; message: string }[];
  /** The constraint engine's reasons (422 on approve). */
  errors: string[];
  /** True when the server definitely did not act (the intention is over). Network failures and 5xx are not definite. */
  definite: boolean;
}

export function describeContentProblem(e: unknown, what: string): ContentProblem {
  if (!(e instanceof ApiError)) return { kind: "network", message: `${what}: couldn't reach the server, so it is not known whether anything was saved. Check the list below before trying again.`, issues: [], errors: [], definite: false };
  const body = e.body ?? {};
  const issues = (Array.isArray(body.issues) ? body.issues : []).filter(isObject).map((i) => ({ field: tidy(i.field, 60), message: safeMessage(i.message, 300) }));
  const errors = strings(body.errors, 400);
  const message = safeMessage(e.message, 400);
  const base = { issues, errors, definite: true };
  if (e.code === "CONSTRAINTS_FAILED" || (e.status === 422 && errors.length > 0)) return { ...base, kind: "constraints", message: `${what}: the content fails the constraint check, so it cannot be approved. Nothing was approved.` };
  if (e.code === "IDEMPOTENCY_CONFLICT") return { ...base, kind: "idempotency_conflict", message: `${what}: this request key was already used for different content. Nothing new was saved.` };
  if (e.code === "NOT_EDITABLE") return { ...base, kind: "not_editable", message: `${what}: ${message}` };
  if (e.code === "CLIENT_NOT_ACTIVE") return { ...base, kind: "inactive", message: `${what}: this client is not active, so content cannot be added.` };
  if (e.status === 422 || e.status === 400) return { ...base, kind: "validation", message: `${what}: ${message}` };
  if (e.status === 404) return { ...base, kind: "notfound", message: `${what}: not found (it may have been removed). Reload.` };
  if (e.status === 401 || e.status === 403) return { ...base, kind: "unauthorized", message: `${what}: not allowed (${e.status}). Nothing was saved.` };
  if (e.status >= 500) return { ...base, definite: false, kind: "server", message: `${what}: the server failed (HTTP ${e.status}), so it is not known whether anything was saved. Check the list below before trying again.` };
  return { ...base, kind: "other", message: `${what}: ${message}` };
}
